import {
  ForwardedRef,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";
import { Controller, useForm } from "react-hook-form";

import {
  checkHasResponse,
  SubmissionSyncStatus,
} from "@smart/bridge-types-basic";
import { StaffDetails } from "@smart/bridge-types-basic/src/appointment";
import { Loading, UploadFileProps } from "@smart/itops-sb-design-system-dom";
import {
  entriesOf,
  fromEntries,
  isNotNullOrUndefined,
} from "@smart/itops-utils-basic";

import {
  fieldName,
  fieldValidation,
  getRequiredMessage,
  renderOrderedItems,
  useLoadMatterFields,
  useOrderedErrors,
  useOrderedItems,
  Visibility,
} from "../../hooks";
import {
  AutoFillStatus,
  ConnectionItem,
  FieldItem,
  GroupItem,
  IntakeFormSectionRef,
  LoadResponses,
  LookupOptions,
  ResponseSubmitter,
  SectionItem,
  SubmissionItem,
  TeamItem,
  Toast,
} from "../../types";
import { IntakeField } from "../field";
import {
  FieldComponentOverrideProps,
  FieldProps,
  FieldsList,
} from "../field-components";
import { IntakeGroup, IntakeGroupFieldSet } from "../group";
import { IntakeSection } from "../section";

export type IntakeFormSectionProps = {
  connection?: ConnectionItem;
  autoFillSection?: SectionItem;
  visibleSections: SectionItem[];
  selected: SectionItem | undefined;
  groups: GroupItem[];
  fields: FieldItem[];
  onSelect: (section: SectionItem) => void;
  onSummary: () => void;
  loadResponses: LoadResponses;
  submitResponses: ResponseSubmitter;
  setResponses: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  setStatus: (status: SubmissionItem["status"]) => void;
  team: TeamItem;
  sidebar: boolean;
  setSidebar: (sidebar: boolean) => void;
  responses: Record<string, any>;
  visibility: Visibility;
  disabled?: boolean;
  isPreview?: boolean;
  showPaymentPlaceholder?: boolean;
  previewOptions?: {
    previewErrorToggle?: [
      boolean,
      React.Dispatch<React.SetStateAction<boolean>>,
    ];
    previewResponses?: [
      Record<string, any>,
      React.Dispatch<React.SetStateAction<Record<string, any>>>,
    ];
  };
  isUpdating?: boolean;
  setVisited: (section: SectionItem) => void;
  staffDetails: StaffDetails[];
  submissionSyncStatus?: SubmissionSyncStatus | null;
  showConnectionBanner?: [
    boolean,
    React.Dispatch<React.SetStateAction<boolean>>,
  ];
  autoFillStatus?: AutoFillStatus;
  loading?: boolean;
  aiFillFromMatterInfo?: boolean;
  fieldComponentOverride?: (
    fieldProps: FieldProps & FieldComponentOverrideProps,
  ) => React.ReactNode | undefined;
  setToasts?: (toasts: Toast[]) => void;
  isTeamHidden?: boolean;
  isWebEnquiry?: boolean;
  setFields?: (setFn: (fields: FieldItem[]) => FieldItem[]) => void;
  isPrint?: boolean;
} & LookupOptions & { uploadFileProps: UploadFileProps };

export const IntakeFormSection = forwardRef(
  (
    {
      addressLookup,
      fileLookup,
      connection,
      autoFillSection,
      visibleSections,
      selected,
      groups,
      fields,
      onSelect,
      onSummary,
      loadResponses,
      submitResponses,
      setResponses,
      setStatus,
      team,
      sidebar,
      setSidebar,
      responses,
      visibility,
      disabled,
      previewOptions,
      isUpdating,
      setVisited,
      uploadFileProps,
      staffDetails,
      submissionSyncStatus,
      showConnectionBanner,
      autoFillStatus,
      loading,
      aiFillFromMatterInfo,
      isPreview,
      showPaymentPlaceholder,
      fieldComponentOverride,
      setToasts,
      isTeamHidden,
      isWebEnquiry,
      setFields,
      isPrint,
    }: IntakeFormSectionProps,
    forwardedRef: ForwardedRef<IntakeFormSectionRef>,
  ) => {
    const orderedItems = useOrderedItems({
      selected,
      groups,
      fields,
      visibility,
      responses,
    });

    const form = useForm({
      defaultValues: previewOptions?.previewResponses?.[0] || responses,
    });

    // For Safari only
    const scrollPositionRef = useRef<{
      scrollContainer: HTMLElement | undefined;
      scrollPosition: number | undefined;
    }>({ scrollContainer: undefined, scrollPosition: undefined });
    const errorsRef = useRef<HTMLDivElement>(null);
    const hideErrors = previewOptions?.previewErrorToggle?.[0];
    const showNext = () => {
      if (connection?.status === "error" && !hideErrors) return;

      const selectedIndex = selected
        ? visibleSections.findIndex((s) => s.uri === selected?.uri)
        : -1;
      if (selectedIndex !== -1) setVisited(visibleSections[selectedIndex]);

      if (visibleSections.length === 1) setStatus("completed");
      const next = visibleSections[selectedIndex + 1];
      if (next) {
        onSelect(next);
      } else {
        onSummary();
      }
    };

    const onSubmit = (options?: { nextAction?: () => void; skip?: boolean }) =>
      options?.skip
        ? options.nextAction || (() => {})
        : form.handleSubmit(
            async (values) => {
              if (visibleSections.length === 1) {
                const status = "completed";
                const result = await submitResponses({
                  responses: values,
                  status,
                });
                if (result?.error) {
                  setToasts?.([
                    {
                      text: result.error,
                      palette: "destructive",
                    },
                  ]);
                } else if (result?.operationIds) {
                  setResponses((current) => ({ ...current, ...values }));
                  setStatus(status);
                }
                setVisited(visibleSections[0]);
              } else if (options?.nextAction) {
                options.nextAction();
              }
            },
            (errors) => {
              if (entriesOf(errors).length > 1) {
                setTimeout(() => errorsRef?.current?.focus(), 5);
              }
            },
          );

    const hasRequired = orderedItems.some((i) =>
      i.type === "field"
        ? i.field.mandatory
        : i.fields.some((f) => f.mandatory),
    );
    const errors = useOrderedErrors({ orderedItems }, form.formState);
    const hasDynamicOptions = useMemo(
      () => fields.some((f) => f.hasDynamicOptions),
      [fields],
    );

    useLoadMatterFields(
      {
        submissionSyncStatus,
        fn: () => {
          entriesOf(responses).forEach(([fieldUri, value]) => {
            const currentValue = form.getValues(fieldUri);
            if (
              !currentValue ||
              (Array.isArray(currentValue) &&
                !currentValue.filter(Boolean).length)
            ) {
              const field = fields.find((f) => f.uri === fieldUri);
              if (!field) return;

              const isGroup = !!field.groupUri;
              if (Array.isArray(value) && isGroup) {
                value.forEach((v, index) => {
                  form.setValue(fieldName({ field, index }), v);
                });
              } else {
                form.setValue(fieldName({ field }), value);
              }
            }
          });
        },
      },
      [responses],
    );

    useEffect(() => {
      if (previewOptions?.previewResponses) {
        previewOptions.previewResponses[1]((prevPreviewResponses) => ({
          ...prevPreviewResponses,
          ...responses,
        }));
      }
    }, [responses]);

    useEffect(() => {
      if (selected?.validateOnDisplay) {
        form.trigger().catch(console.error);
      }
    }, []);

    useImperativeHandle(forwardedRef, () => ({
      submitForm: onSubmit(),
      errors,
      connection,
    }));

    const selectedIndex = visibleSections.findIndex(
      (s) => s.uri === selected?.uri,
    );

    const showBack = () => {
      if (selectedIndex > 0) {
        onSelect(visibleSections[selectedIndex - 1]);
      } else if (selectedIndex === 0 && autoFillSection) {
        onSelect(autoFillSection);
      }
    };

    const getValidationRules = (field: FieldItem) => {
      if (disabled || field.type === "info") {
        return undefined;
      }

      const optionalSpecialField =
        ["appointment", "payment"].includes(field.type) && !field.mandatory;

      return {
        required: field.mandatory ? getRequiredMessage(field.type) : undefined,
        validate: optionalSpecialField
          ? undefined
          : fieldValidation[field.type],
      };
    };

    const isNonRepeatableField = (field: FieldItem) => field.type === "payment";

    const autoFillInProgress = autoFillStatus === "filling";

    return (
      <>
        {form.formState.isSubmitting && <Loading />}
        <IntakeSection
          connection={connection}
          team={team}
          sidebar={sidebar}
          setSidebar={setSidebar}
          autoFillSection={autoFillSection}
          sections={visibleSections}
          selected={{
            ...selected,
            uri: selected?.uri || "",
            hasRequired,
          }}
          onSubmit={hideErrors ? showNext : onSubmit({ nextAction: showNext })}
          hasPayment={Object.values(responses).some((r) => !!r?.chargeRequest)}
          onPreviousButton={
            hideErrors
              ? showBack
              : onSubmit({
                  nextAction: showBack,
                  skip: !isUpdating || selectedIndex === 0,
                })
          }
          onNextButton={
            disabled ? onSubmit({ nextAction: showNext }) : undefined
          }
          onSubmitButton={
            disabled ? onSubmit({ nextAction: showNext }) : undefined
          }
          errors={errors}
          isForm={!disabled}
          errorsRef={errorsRef}
          previewErrorToggle={previewOptions?.previewErrorToggle}
          showConnectionBanner={showConnectionBanner}
          retrySubmitResponses={() => {
            const values = form.getValues();
            submitResponses({
              responses: values,
            })
              .then((result) => {
                if (result?.operationIds) {
                  setResponses((current) => ({
                    ...current,
                    ...values,
                  }));
                }
              })
              .catch(() => {});
          }}
          autoFillStatus={autoFillStatus}
          loading={loading}
          aiFillFromMatterInfo={aiFillFromMatterInfo}
          isPreview={isPreview}
          setToasts={setToasts}
          isTeamHidden={isTeamHidden}
          isWebEnquiry={isWebEnquiry}
          isPrint={isPrint}
        >
          <FieldsList>
            {renderOrderedItems({
              orderedItems,
              responses,
              renderGroup: ({
                key,
                group,
                fields: groupFields,
                length,
                children,
              }) => (
                <IntakeGroup
                  key={key}
                  scrollPositionRef={scrollPositionRef}
                  group={group}
                  onAdd={
                    group.repeatable && length <= (group.maxRepeat || 30)
                      ? () => {
                          if (disabled) return;

                          const field = groupFields.filter(
                            (f) => f.type !== "payment",
                          )[0];
                          if (!field) return;

                          const fieldValue = form.getValues(field.uri);
                          const toUpdate = Array.isArray(fieldValue)
                            ? fieldValue.slice(0)
                            : [null];
                          toUpdate.splice(length, 0, null);
                          const updated = {
                            [field.uri]: toUpdate,
                          };

                          form.setValue(field.uri, toUpdate);
                          submitResponses({
                            responses: updated,
                            lastUpdatedFieldUri: field.uri,
                            lastUpdatedSectionUri: selected?.uri,
                          })
                            .then((result) => {
                              if (result?.operationIds) {
                                setResponses((current) => ({
                                  ...current,
                                  ...updated,
                                }));
                              }
                            })
                            .catch(() => {});
                        }
                      : undefined
                  }
                >
                  <FieldsList>{children}</FieldsList>
                </IntakeGroup>
              ),
              renderGroupFieldSet: ({
                key,
                index,
                length,
                group,
                fields: groupFields,
                children,
              }) => (
                <IntakeGroupFieldSet
                  key={key}
                  group={group}
                  index={index}
                  onRemove={
                    group.repeatable && length > (group.minRepeat || 1)
                      ? () => {
                          if (disabled) return;

                          const updated: Record<string, any> = {};
                          for (const field of groupFields.filter(
                            (f) => !isNonRepeatableField(f),
                          )) {
                            const fieldValue = form.getValues(field.uri);
                            if (Array.isArray(fieldValue)) {
                              const toUpdate = fieldValue.slice(0);
                              toUpdate.splice(index, 1);
                              updated[field.uri] = toUpdate;
                              form.setValue(field.uri, toUpdate);
                            }
                          }

                          submitResponses({
                            responses: updated,
                            lastUpdatedFieldUri: groupFields[0].uri,
                            lastUpdatedSectionUri: selected?.uri,
                          })
                            .then((result) => {
                              if (result?.operationIds) {
                                setResponses((current) => ({
                                  ...current,
                                  ...updated,
                                }));
                              }
                            })
                            .catch(() => {});
                        }
                      : undefined
                  }
                >
                  {children}
                </IntakeGroupFieldSet>
              ),
              renderField: ({ key, field, group, index }) => {
                // Render only one field if it's non repeatable
                if (isNonRepeatableField(field) && index && index > 0)
                  return null;

                // Check if the field is repeatable and has in-group conditions to meet
                if (
                  group?.repeatable &&
                  !visibility.visibleRepeatableFields[field.uri]?.[index!]
                ) {
                  return null;
                }

                return (
                  <Controller
                    key={key}
                    control={form.control}
                    name={fieldName({
                      field,
                      index: isNonRepeatableField(field) ? undefined : index,
                    })}
                    rules={getValidationRules(field)}
                    render={({
                      field: { ref, value, onChange, onBlur },
                      fieldState: { error },
                    }) => {
                      const isLoadingMatterInfo = !!(
                        submissionSyncStatus === "loading" &&
                        field.layout &&
                        field.field &&
                        !checkHasResponse(field.type, value)
                      );
                      return (
                        <IntakeField
                          key={key}
                          innerRef={ref}
                          addressLookup={addressLookup}
                          fileLookup={fileLookup}
                          uploadFileProps={uploadFileProps}
                          loadResponses={loadResponses}
                          field={field}
                          index={index}
                          value={value}
                          isPreview={isPreview}
                          showPaymentPlaceholder={showPaymentPlaceholder}
                          isPrint={isPrint}
                          error={hideErrors ? undefined : error?.message}
                          loading={isLoadingMatterInfo}
                          disabled={
                            disabled ||
                            isLoadingMatterInfo ||
                            autoFillInProgress
                          }
                          onBlur={() => {
                            if (disabled) return onBlur();

                            const updated = {
                              [field.uri]: form.getValues(field.uri),
                            };

                            const willBeHidden = visibility.willBeHidden(
                              updated,
                              group,
                              index,
                            );
                            const fieldMap = fromEntries(
                              fields.map((f) => [f.uri, f]),
                            );
                            for (const uri of willBeHidden) {
                              if (fieldMap[uri]?.type !== "payment") {
                                // Update responses to affecting fields within the same group
                                const fieldValue = form.getValues(uri);
                                if (fieldMap[uri].groupUri && group) {
                                  if (
                                    group.uri === fieldMap[uri].groupUri &&
                                    index !== undefined &&
                                    Array.isArray(fieldValue) &&
                                    isNotNullOrUndefined(fieldValue[index])
                                  ) {
                                    fieldValue[index] = null;
                                    updated[uri] = fieldValue;
                                    form.setValue(uri, fieldValue);
                                  }
                                } else {
                                  updated[uri] = null;
                                  form.setValue(uri, null);
                                }
                              }
                            }

                            submitResponses({
                              responses: updated,
                              lastUpdatedFieldUri: field.uri,
                              lastUpdatedSectionUri: selected?.uri,
                              hasDynamicOptions,
                            })
                              .then((result) => {
                                if (result?.operationIds)
                                  setResponses((current) => ({
                                    ...current,
                                    ...updated,
                                  }));
                                setFields?.((current) =>
                                  result?.fieldsWithDynamicOptions
                                    ? current.map((existingField) => {
                                        const fieldWithDynamicOptions =
                                          result.fieldsWithDynamicOptions?.find(
                                            (f) => f.uri === existingField.uri,
                                          );
                                        return (
                                          fieldWithDynamicOptions ||
                                          existingField
                                        );
                                      })
                                    : current,
                                );
                              })
                              .catch(() => {});

                            return onBlur();
                          }}
                          onChange={disabled ? () => {} : onChange}
                          staffDetails={staffDetails}
                          fieldComponentOverride={fieldComponentOverride}
                          groups={groups}
                          fields={fields}
                          visibility={visibility}
                          submitResponses={submitResponses}
                          responses={responses}
                        />
                      );
                    }}
                  />
                );
              },
            })}
          </FieldsList>
        </IntakeSection>
      </>
    );
  },
);
